import { useEffect, useRef, useState } from 'react';
import { Check, MapPin } from 'lucide-react';
import { useI18n } from '../lib/i18n';

/* eslint-disable @typescript-eslint/no-explicit-any */

/*
 * Bootstrap oficial de Google Maps (idempotente): define
 * google.maps.importLibrary y carga las librerías de forma perezosa. Es la
 * forma soportada para la API nueva de Places. Se ejecuta una sola vez aunque
 * el módulo recargue por HMR.
 */
let mapsBootstrapped = false;
function loadMaps(key: string): void {
  if ((window as any).google?.maps?.importLibrary || mapsBootstrapped) return;
  mapsBootstrapped = true;
  /* eslint-disable no-cond-assign, no-sequences, no-async-promise-executor, @typescript-eslint/no-unused-expressions, prefer-const */
  ((g: any) => {
    let h: any, a: any, k: any, p = 'The Google Maps JavaScript API', c = 'google', l = 'importLibrary',
      q = '__ib__', m = document, b: any = window;
    b = b[c] || (b[c] = {});
    const d = b.maps || (b.maps = {}), r = new Set<string>(), e = new URLSearchParams(),
      u = () => h || (h = new Promise<void>(async (f, n) => {
        await (a = m.createElement('script'));
        e.set('libraries', [...r] + '');
        for (k in g) e.set(k.replace(/[A-Z]/g, (t: string) => '_' + t[0].toLowerCase()), g[k]);
        e.set('callback', c + '.maps.' + q);
        a.src = `https://maps.${c}apis.com/maps/api/js?` + e;
        d[q] = f;
        a.onerror = () => (h = n(Error(p + ' could not load.')));
        a.nonce = (m.querySelector('script[nonce]') as any)?.nonce || '';
        m.head.append(a);
      }));
    d[l] ? console.warn(p + ' only loads once. Ignoring:', g) : (d[l] = (f: any, ...n: any[]) => r.add(f) && u().then(() => d[l](f, ...n)));
  })({ key, v: 'weekly' });
  /* eslint-enable no-cond-assign, no-sequences, no-async-promise-executor, @typescript-eslint/no-unused-expressions, prefer-const */
}

type Suggestion = { id: string; text: string; toPlace: () => any };

/**
 * Campo de dirección con autocompletado + verificación (Google Places, API
 * nueva: AutocompleteSuggestion). Input y dropdown propios para tener control y
 * estilo consistente. Al elegir una sugerencia, la dirección queda VERIFICADA
 * (✓) con su formatted_address. Sin VITE_GOOGLE_MAPS_KEY, cae a input normal.
 */
export function AddressAutocomplete({
  label,
  value,
  onChange,
  onPlace,
}: {
  label: string;
  value: string;
  onChange: (v: string, verified: boolean) => void;
  /** ClaimCam (2026-10-10): la dirección elegida CON sus coordenadas (para "proyecto por dirección" y el "cerca de mí"). */
  onPlace?: (p: { address: string; lat: number | null; lng: number | null }) => void;
}) {
  const { t } = useI18n();
  const key = import.meta.env.VITE_GOOGLE_MAPS_KEY as string | undefined;
  const [query, setQuery] = useState(value);
  const [suggestions, setSuggestions] = useState<Suggestion[]>([]);
  const [open, setOpen] = useState(false);
  const [verified, setVerified] = useState(false);
  const [apiError, setApiError] = useState(false);
  const tokenRef = useRef<any>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => { if (key) loadMaps(key); }, [key]);

  // Cierra el dropdown al hacer click fuera.
  useEffect(() => {
    const onDocClick = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDocClick);
    return () => document.removeEventListener('mousedown', onDocClick);
  }, []);

  async function fetchSuggestions(input: string) {
    if (!key || input.trim().length < 3) { setSuggestions([]); setOpen(false); return; }
    try {
      const places: any = await (window as any).google.maps.importLibrary('places');
      if (!tokenRef.current) tokenRef.current = new places.AutocompleteSessionToken();
      const { suggestions: raw } = await places.AutocompleteSuggestion.fetchAutocompleteSuggestions({
        input,
        sessionToken: tokenRef.current,
      });
      const mapped: Suggestion[] = (raw || [])
        .filter((s: any) => s.placePrediction)
        .map((s: any) => ({
          id: s.placePrediction.placeId,
          text: s.placePrediction.text.text as string,
          toPlace: () => s.placePrediction.toPlace(),
        }));
      setSuggestions(mapped);
      setOpen(mapped.length > 0);
      setApiError(false);
    } catch (e) {
      // No tragar el error en silencio: casi siempre es la key restringida por
      // referrer (API_KEY_HTTP_REFERRER_BLOCKED) o la Places API (New) sin habilitar.
      // eslint-disable-next-line no-console
      console.warn('[AddressAutocomplete] Google Places falló (revisar restricciones de la API key):', e);
      setSuggestions([]);
      setOpen(false);
      setApiError(true);
    }
  }

  function onInput(v: string) {
    setQuery(v);
    setVerified(false);
    onChange(v, false);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => fetchSuggestions(v), 250);
  }

  async function pick(s: Suggestion) {
    setOpen(false);
    try {
      const place = s.toPlace();
      await place.fetchFields({ fields: onPlace ? ['formattedAddress', 'location'] : ['formattedAddress'] });
      const addr = (place.formattedAddress as string) || s.text;
      setQuery(addr);
      setVerified(true);
      onChange(addr, true);
      if (onPlace) {
        const loc = place.location as { lat?: () => number; lng?: () => number } | null | undefined;
        const lat = typeof loc?.lat === 'function' ? loc.lat() : null, lng = typeof loc?.lng === 'function' ? loc.lng() : null;
        onPlace({ address: addr, lat: Number.isFinite(lat as number) ? (lat as number) : null, lng: Number.isFinite(lng as number) ? (lng as number) : null });
      }
    } catch {
      setQuery(s.text);
      setVerified(true);
      onChange(s.text, true);
      onPlace?.({ address: s.text, lat: null, lng: null });
    }
    tokenRef.current = null; // nueva sesión tras elegir (best practice de billing)
  }

  return (
    <label className="field" style={{ position: 'relative' }} ref={boxRef as any}>
      {label}
      {verified && (
        <span style={{ color: 'var(--green)', marginLeft: 8, fontWeight: 'var(--fw-semibold)', fontSize: 'var(--fs-2xs)' }}>
          <Check size={12} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {t('addr.verified')}
        </span>
      )}
      <input
        className="input"
        value={query}
        autoComplete="off"
        placeholder={key ? t('addr.placeholder') : ''}
        onChange={(e) => onInput(e.target.value)}
        onFocus={() => { if (suggestions.length) setOpen(true); }}
      />
      {apiError && (
        <span className="muted" style={{ fontSize: 'var(--fs-2xs)', marginTop: 2 }}>{t('addr.unavailable')}</span>
      )}
      {open && suggestions.length > 0 && (
        <ul
          style={{
            position: 'absolute', top: '100%', left: 0, right: 0, zIndex: 50, margin: '4px 0 0',
            padding: 4, listStyle: 'none', background: 'var(--card, #fff)',
            border: '1px solid var(--border)', borderRadius: 8,
            boxShadow: '0 8px 24px rgba(0,0,0,.12)', maxHeight: 260, overflowY: 'auto',
          }}
        >
          {suggestions.map((s) => (
            <li key={s.id}>
              <button
                type="button"
                onMouseDown={(e) => { e.preventDefault(); pick(s); }}
                style={{
                  display: 'block', width: '100%', textAlign: 'left', padding: '8px 10px',
                  border: 'none', background: 'transparent', cursor: 'pointer', borderRadius: 6,
                  fontSize: 'var(--fs-sm)', color: 'var(--text, #1a1a1a)',
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = 'var(--bg, #f3f4f6)')}
                onMouseLeave={(e) => (e.currentTarget.style.background = 'transparent')}
              >
                <MapPin size={13} strokeWidth={1.75} style={{ verticalAlign: 'text-bottom' }} /> {s.text}
              </button>
            </li>
          ))}
        </ul>
      )}
    </label>
  );
}
